'use client';

import { motion } from 'motion/react';

export default function Dashboard() {
  return (
    <section className="bg-[#0f0f0f] py-24 px-6 md:px-12 lg:px-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-4 text-[10px] font-bold tracking-widest text-[#b4ff00]"
            >
              ENGINE SHOWCASE
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="mb-4 text-4xl font-black tracking-tight text-white md:text-5xl"
            >
              COMMAND THE ARENA
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="max-w-xl text-sm text-zinc-400"
            >
              Our unified dashboard replaces messy spreadsheets with high-performance data visualization. Track brackets, players, and broadcast feeds in real-time.
            </motion.p>
          </div>
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-2 rounded-sm bg-zinc-800 px-6 py-3 text-xs font-bold text-white transition-colors hover:bg-zinc-700"
          >
            VIEW DASHBOARD DEMO <span className="flex h-4 w-4 items-center justify-center rounded bg-[#b4ff00] text-black">↗</span>
          </motion.button>
        </div>

        {/* Dashboard Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-[#141414] shadow-2xl md:flex-row"
        >
          {/* Sidebar */}
          <div className="w-full border-b border-zinc-800 p-6 md:w-64 md:border-b-0 md:border-r">
            <div className="mb-8 h-4 w-24 rounded bg-zinc-800" />
            <div className="space-y-4">
              <div className="h-2 w-full rounded bg-zinc-800" />
              <div className="h-2 w-3/4 rounded bg-[#b4ff00]" />
              <div className="h-2 w-5/6 rounded bg-zinc-800" />
              <div className="h-2 w-4/5 rounded bg-zinc-800" />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 md:p-8">
            {/* Top Stats */}
            <div className="mb-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-[#0a0a0a] p-4">
                <div className="mb-2 text-[10px] font-bold text-zinc-500">ACTIVE TOURNAMENTS</div>
                <div className="text-2xl font-bold text-white">1,204</div>
              </div>
              <div className="rounded-xl bg-[#0a0a0a] p-4">
                <div className="mb-2 text-[10px] font-bold text-zinc-500">GLOBAL REACH</div>
                <div className="text-2xl font-bold text-white">92 Countries</div>
              </div>
              <div className="rounded-xl bg-[#0a0a0a] p-4">
                <div className="mb-2 text-[10px] font-bold text-zinc-500">LIVE BROADCASTS</div>
                <div className="flex items-center gap-2 text-2xl font-bold text-white">
                  <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  42
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] font-bold text-zinc-500">
                    <th className="pb-4 font-medium">TOURNAMENT NAME</th>
                    <th className="pb-4 font-medium">TIER</th>
                    <th className="pb-4 font-medium">PARTICIPANTS</th>
                    <th className="pb-4 font-medium">PRIZE POOL</th>
                    <th className="pb-4 font-medium">STATUS</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800/50">
                    <td className="py-4 font-bold text-white">Elite Champions Cup</td>
                    <td className="py-4"><span className="rounded bg-[#b4ff00]/20 px-2 py-1 text-[10px] font-bold text-[#b4ff00]">LEVEL 1</span></td>
                    <td className="py-4">512 Teams</td>
                    <td className="py-4">$50,000</td>
                    <td className="py-4 text-[#b4ff00] font-bold text-xs">STREAMING LIVE</td>
                  </tr>
                  <tr className="border-b border-zinc-800/50">
                    <td className="py-4 font-bold text-white">Global Rivals Open</td>
                    <td className="py-4"><span className="rounded bg-[#b4ff00]/20 px-2 py-1 text-[10px] font-bold text-[#b4ff00]">LEVEL 2</span></td>
                    <td className="py-4">1,024 Teams</td>
                    <td className="py-4">$12,500</td>
                    <td className="py-4 text-xs font-bold text-zinc-500">IN PROGRESS</td>
                  </tr>
                  <tr>
                    <td className="py-4 font-bold text-white">U21 Development League</td>
                    <td className="py-4"><span className="rounded bg-zinc-800 px-2 py-1 text-[10px] font-bold text-zinc-400">LOCAL</span></td>
                    <td className="py-4">32 Teams</td>
                    <td className="py-4">N/A</td>
                    <td className="py-4 text-xs font-bold text-zinc-500">REGISTRATION</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
